import { fetchWithRetry } from 'utils/fetchWithRetry';
import { HttpRequestMethodTypes } from 'types';
import { getGoSearchHost, isServerSide } from 'utils/host-config';
import type { CommercialSearchPredictionsParams } from './types';
import { CommercialSearchPredictionResponse, CommercialSearchPredictionRequest } from '@zoocasa/go-search';

/**
 * Fetches commercial search predictions from the go-search service
 * @param params Commercial search parameters including query string and optional filters
 * @param host The host to use for the API request
 * @returns commercial search predictions response or null if the request fails
 */
export async function fetchCommercialSearchPredictions(
  params: CommercialSearchPredictionsParams,
  host: string = getGoSearchHost(isServerSide())
) {
  try {
    const commercialSearchPredictionsUrl = '/api/commercial/search-prediction';
    const fullUrl = `${host}${commercialSearchPredictionsUrl}`;
    
    // Create Protobuf request
    const request = CommercialSearchPredictionRequest.fromPartial({
      query: params.query,
      limit: params.limit,
      country: params.country,
    });
    
    // Encode the request as Protobuf binary
    const requestBody = CommercialSearchPredictionRequest.encode(request).finish();
    
    const response = await fetchWithRetry(fullUrl, { 
      method: HttpRequestMethodTypes.POST, 
      body: requestBody,
    });

    if (!response.ok) {
      return null;
    }

    // The API returns Protobuf binary data
    try {
      // Get response as blob then array buffer for Protobuf decoding
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      
      // Decode the Protobuf response using the proper decoder
      const decodedResponse = CommercialSearchPredictionResponse.decode(new Uint8Array(buffer));
      return decodedResponse;
    } catch (_protobufError) {
      return null;
    }
  } catch (_error) {
    return null;
  }
}